import React, { useState, useCallback } from 'react';
import {
    Box,
    Typography,
    Button,
    Stack,
    Paper,
    Menu,
    MenuItem,
    TextField,
    Divider,
    Chip,
    InputAdornment,
    CircularProgress,
    Card
} from '@mui/material';
import AddIcon from '@mui/icons-material/Add';
import LocationOnIcon from '@mui/icons-material/LocationOn';
import BusinessIcon from '@mui/icons-material/Business';
import PersonIcon from '@mui/icons-material/Person';
import PhoneIcon from '@mui/icons-material/Phone';
import EmailIcon from '@mui/icons-material/Email';
import SearchIcon from '@mui/icons-material/Search';
import { toast } from 'react-toastify';
import { useFormContext } from 'react-hook-form';
import {VendorSelectSectionProps } from '../../../types';

// Combined entity type for both parties and vendors
interface CombinedEntity {
    _id: string;
    name: string;
    type: 'party' | 'vendor';
    contact?: {
        phone?: Array<{ label?: string; phoneNo?: string | number }>;
        email?: string[];
    };
    address?: {
        line1?: string;
        city?: string;
        state?: string;
        country?: string;
        pinCode?: string;
    };
    gstNumber?: string;
    panNumber?: string;
    location?: string;
    billingAddress?: string;
    originalData: any;
}

// Updated props interface
interface ExtendedVendorSelectSectionProps extends VendorSelectSectionProps {
    partyFormName?: string; // Form field name for party
    vendorFormName?: string; // Form field name for vendor
}

const VendorSelectSection: React.FC<ExtendedVendorSelectSectionProps> = ({
    billToLabel = 'Select Vendor/Party',
    onAddNewVendor,
    loading = false,
    error,
    totalPayableLabel='Total Outstanding',
    compact = false,
    sx = {},
    request,
    apiParams = {},
    partyFormName = 'party', // Default party field name
    vendorFormName = 'vendor', // Default vendor field name
    sendVendorIdOnly = true,
    showTotalDue = false,
    totalDue
}) => {
    const [anchorEl, setAnchorEl] = useState<null | HTMLElement>(null);
    const [searchTerm, setSearchTerm] = useState('');
    const [combinedEntities, setCombinedEntities] = useState<CombinedEntity[]>([]);
    const [isLoading, setIsLoading] = useState(false);
    const [apiError, setApiError] = useState<string | null>(null);
    const [selectedEntity, setSelectedEntity] = useState<CombinedEntity | null>(null);

    // React Hook Form integration
    const { setValue, getValues, watch } = useFormContext?.() || {};

    // Watch both party and vendor fields
    const formParty = partyFormName ? watch?.(partyFormName) : null;
    const formVendor = vendorFormName ? watch?.(vendorFormName) : null;

    // Determine which entity is selected based on form values
    const getSelectedEntity = (): CombinedEntity | null => {
        if (formParty && typeof formParty === 'string') {
            return combinedEntities.find(e => e._id === formParty && e.type === 'party') || null;
        }
        if (formVendor && typeof formVendor === 'string') {
            return combinedEntities.find(e => e._id === formVendor && e.type === 'vendor') || null;
        }
        return selectedEntity;
    };

    const currentEntity = getSelectedEntity();

    // Fetch both parties and vendors from API when menu opens
    const fetchEntities = useCallback(async () => {
        setIsLoading(true);
        setApiError(null);

        try {
            // Fetch vendors
            const vendorsParams = new URLSearchParams();
            Object.entries(apiParams).forEach(([key, value]) => {
                if (value !== undefined && value !== null) {
                    vendorsParams.append(key, value.toString());
                }
            });
            if (searchTerm.trim()) {
                vendorsParams.append('search', searchTerm.trim());
            }

            // Fetch parties
            const partiesParams = new URLSearchParams();
            Object.entries(apiParams).forEach(([key, value]) => {
                if (value !== undefined && value !== null) {
                    partiesParams.append(key, value.toString());
                }
            });
            if (searchTerm.trim()) {
                partiesParams.append('search', searchTerm.trim());
            }

            // Make both API calls in parallel
            const [vendorsRes, partiesRes] = await Promise.all([
                request({
                    url: `vendor/get-all-vendors?${vendorsParams.toString()}`,
                    method: 'get',
                }),
                request({
                    url: `party/get-all-parties?${partiesParams.toString()}`,
                    method: 'get',
                })
            ]);

            const combined: CombinedEntity[] = [];

            // Process vendors
            if (vendorsRes.status === "success" && vendorsRes.vendors) {
                vendorsRes.vendors.forEach((vendor: any) => {
                    combined.push({
                        _id: vendor._id,
                        name: vendor.vendorName || 'Unnamed Vendor',
                        type: 'vendor',
                        contact: vendor.contact,
                        address: vendor.address,
                        gstNumber: vendor.gstNumber,
                        panNumber: vendor.panNumber,
                        location: vendor.location,
                        billingAddress: vendor.billingAddress,
                        originalData: vendor
                    });
                });
            }

            // Process parties
            if (partiesRes.status === "success" && partiesRes.parties) {
                partiesRes.parties.forEach((party: any) => {
                    combined.push({
                        _id: party._id,
                        name: party.partyName || 'Unnamed Party',
                        type: 'party',
                        contact: party.contact,
                        address: party.address,
                        gstNumber: party.gstNumber,
                        panNumber: party.panNumber,
                        location: party.location,
                        billingAddress: party.billingAddress,
                        originalData: party
                    });
                });
            }

            // Sort combined list alphabetically by name
            combined.sort((a, b) => a.name.localeCompare(b.name));

            setCombinedEntities(combined);

            if (combined.length === 0) {
                setApiError('No vendors or parties found');
            }
        } catch (error: any) {
            toast.error('Error fetching data:', error);
            setApiError('Failed to load data');
            setCombinedEntities([]);
        } finally {
            setIsLoading(false);
        }
    }, [request, apiParams, searchTerm]);

    // Filter entities based on search locally
    const filteredEntities = searchTerm.trim()
        ? combinedEntities.filter(entity =>
            entity.name?.toLowerCase().includes(searchTerm.toLowerCase()) ||
            entity.contact?.phone?.[0]?.phoneNo?.toString().includes(searchTerm) ||
            entity.contact?.email?.[0]?.toLowerCase().includes(searchTerm.toLowerCase())
        )
        : combinedEntities;

    const handleAddClick = async (event: React.MouseEvent<HTMLButtonElement>) => {
        setAnchorEl(event.currentTarget);
        await fetchEntities();
    };

    const handleClose = () => {
        setAnchorEl(null);
        setSearchTerm('');
        setApiError(null);
    };

    const handleEntitySelect = (entity: CombinedEntity) => {
        // Clear both fields first
        if (partyFormName && setValue) {
            setValue(partyFormName, '');
        }
        if (vendorFormName && setValue) {
            setValue(vendorFormName, '');
        }

        // Set the appropriate field based on entity type
        if (entity.type === 'vendor') {
            // Store in vendor field
            if (vendorFormName && setValue) {
                setValue(vendorFormName, entity._id);
                // setValue(`${vendorFormName}Name`, entity.name);
                // setValue(`${vendorFormName}Type`, 'vendor');
                // setValue(`${vendorFormName}Details`, entity.originalData);
            }
        } else {
            // Store in party field
            if (partyFormName && setValue) {
                setValue(partyFormName, entity._id);
                // setValue(`${partyFormName}Name`, entity.name);
                // setValue(`${partyFormName}Type`, 'party');
                // setValue(`${partyFormName}Details`, entity.originalData);
            }
        }

        setSelectedEntity(entity);
        handleClose();
    };

    const handleSearchChange = (e: React.ChangeEvent<HTMLInputElement>) => {
        setSearchTerm(e.target.value);
    };

    // Function to get address from form or entity details
    const getAddress = () => {
        if (currentEntity?.type === 'vendor' && vendorFormName) {
            const billAddr = getValues?.(`${vendorFormName}BillAddress`);
            return billAddr;
        } else if (currentEntity?.type === 'party' && partyFormName) {
            const billAddr = getValues?.(`${partyFormName}BillAddress`);
            return billAddr;
        }
        return currentEntity?.billingAddress || currentEntity?.address?.line1 || currentEntity?.location;
    };

    // Get icon based on entity type
    const getEntityIcon = (type: 'party' | 'vendor') => {
        return type === 'vendor' ? 
            <BusinessIcon fontSize="small" /> : 
            <PersonIcon fontSize="small" />;
    };

    // Get chip color based on entity type
    const getEntityColor = (type: 'party' | 'vendor') => {
        return type === 'vendor' ? 'primary' : 'secondary';
    };

    return (
        <Card sx={{ width: '100%', height: "100%", p: 1, ...sx }}>
            {/* Compact Professional Design */}
            <Box sx={{
                display: 'flex',
                flexDirection: 'column',
                gap: 1.5
            }}>
                {/* Bill To Label */}
                <Typography variant="subtitle2" fontWeight="bold" color="text.primary">
                    {billToLabel}
                </Typography>

                {/* Select Entity Section */}
                {!currentEntity ? (
                    <Paper
                        variant="outlined"
                        sx={{
                            p: 0,
                            borderRadius: 1,
                            borderStyle: 'dashed',
                            borderColor: 'grey.300',
                            bgcolor: 'grey.50',
                            '&:hover': {
                                borderColor: 'primary.main',
                                bgcolor: 'primary.50'
                            }
                        }}
                    >
                        <Button
                            startIcon={<AddIcon />}
                            onClick={handleAddClick}
                            variant="text"
                            size="small"
                            sx={{
                                p: 2,
                                textTransform: 'none',
                                color: 'text.secondary',
                                '&:hover': {
                                    color: 'primary.main'
                                }
                            }}
                            disabled={loading}
                            fullWidth
                        >
                            {loading ? 'Loading...' : `Select ${billToLabel}`}
                        </Button>
                    </Paper>
                ) : (
                    <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1 }}>
                        {/* Selected Entity Card */}
                        <Paper
                            variant="outlined"
                            sx={{
                                p: 1.5,
                                borderRadius: 1,
                                borderColor: `${getEntityColor(currentEntity.type)}.light`,
                                bgcolor: `${getEntityColor(currentEntity.type)}.50`,
                                position: 'relative'
                            }}
                        >
                            {/* Change Button */}
                            <Button onClick={handleAddClick}
                                size='small'
                                sx={{
                                    px: 1,
                                    fontSize: "10px",
                                    position: 'absolute',
                                    top: 8,
                                    right: 8,
                                    bgcolor: 'white',
                                    border: '1px solid',
                                    borderColor: `${getEntityColor(currentEntity.type)}.light`,
                                    '&:hover': {
                                        bgcolor: `${getEntityColor(currentEntity.type)}.50`
                                    }
                                }}>
                                Change
                            </Button>

                            {/* Entity Info */}
                            <Box sx={{ pr: 4 }}>
                                {/* Name with Icon */}
                                <Box display="flex" alignItems="center" gap={1} mb={1}>
                                    {getEntityIcon(currentEntity.type)}
                                    <Typography variant="body2" fontWeight="bold" noWrap>
                                        {currentEntity.name}
                                    </Typography>
                                    <Chip
                                        label={currentEntity.type === 'vendor' ? 'Vendor' : 'Party'}
                                        size="small"
                                        color={getEntityColor(currentEntity.type)}
                                        variant="outlined"
                                        sx={{ height: 20, fontSize: '0.65rem' }}
                                    />
                                </Box>

                                {/* Contact Info */}
                                <Stack spacing={0.5}>
                                    {currentEntity.contact?.phone?.[0]?.phoneNo && (
                                        <Typography variant="caption" display="flex" alignItems="center" gap={0.5}>
                                            <PhoneIcon fontSize="inherit" sx={{ opacity: 0.7 }} />
                                            {currentEntity.contact.phone[0].phoneNo}
                                        </Typography>
                                    )}

                                    {currentEntity.contact?.email?.[0] && (
                                        <Typography variant="caption" display="flex" alignItems="center" gap={0.5}>
                                            <EmailIcon fontSize="inherit" sx={{ opacity: 0.7 }} />
                                            {currentEntity.contact.email[0]}
                                        </Typography>
                                    )}

                                    {currentEntity.gstNumber && (
                                        <Typography variant="caption" fontWeight="medium" color="primary">
                                            GST: {currentEntity.gstNumber}
                                        </Typography>
                                    )}
                                </Stack>
                            </Box>
                        </Paper>

                        {/* Billing Address */}
                        {getAddress() && (
                            <Box>
                                <Typography variant="caption" fontWeight="medium" color="text.secondary" display="block" mb={0.5}>
                                    Billing Address
                                </Typography>
                                <Paper
                                    variant="outlined"
                                    sx={{
                                        p: 1,
                                        borderRadius: 1,
                                        bgcolor: 'grey.50'
                                    }}
                                >
                                    <Box display="flex" alignItems="flex-start" gap={0.5}>
                                        <LocationOnIcon color="action" fontSize="small" sx={{ mt: '2px' }} />
                                        <Typography variant="caption" sx={{ flex: 1, lineHeight: 1.4 }}>
                                            {getAddress()}
                                        </Typography>
                                    </Box>
                                </Paper>
                            </Box>
                        )}

                        {/* Total Due Display */}
                        {showTotalDue && totalDue !== undefined && (
                            <Card sx={{ mt: 0, p: 1.5, bgcolor: 'info.50', display: "flex", alignItems: 'center', gap: 1 }}>
                                <Typography variant="caption" color="text.secondary" fontWeight='bold'>
                                    {totalPayableLabel} :
                                </Typography>
                                <Typography variant="h6" color="error.main" fontWeight={600}>
                                    ₹{totalDue}
                                </Typography>
                            </Card>
                        )}
                    </Box>
                )}
            </Box>

            {/* Error Message */}
            {(error || apiError) && (
                <Typography variant="caption" color="error" mt={1} display="block">
                    {error || apiError}
                </Typography>
            )}

            {/* Combined Entities Dropdown Menu */}
            <Menu
                anchorEl={anchorEl}
                open={Boolean(anchorEl)}
                onClose={handleClose}
                PaperProps={{
                    sx: {
                        maxHeight: 400,
                        width: 400,
                        mt: 0.5,
                        boxShadow: '0px 4px 20px rgba(0, 0, 0, 0.15)'
                    }
                }}
            >
                {/* Search Bar */}
                <Box sx={{ p: 1, borderBottom: 1, borderColor: 'divider' }}>
                    <TextField
                        placeholder="Search vendors or parties..."
                        value={searchTerm}
                        onChange={handleSearchChange}
                        size="small"
                        fullWidth
                        InputProps={{
                            startAdornment: (
                                <InputAdornment position="start">
                                    <SearchIcon fontSize="small" />
                                </InputAdornment>
                            ),
                            sx: { fontSize: '0.875rem' }
                        }}
                        autoFocus
                    />
                </Box>

                {/* Loading State */}
                {isLoading && (
                    <Box display="flex" justifyContent="center" p={2}>
                        <CircularProgress size={20} />
                    </Box>
                )}

                {/* Combined List */}
                {!isLoading && (
                    <Box sx={{ maxHeight: 250, overflow: 'auto' }}>
                        {filteredEntities.length === 0 ? (
                            <MenuItem disabled sx={{ py: 2 }}>
                                <Typography variant="body2" color="text.secondary" textAlign="center" width="100%">
                                    {apiError || 'No vendors or parties found'}
                                </Typography>
                            </MenuItem>
                        ) : (
                            filteredEntities.map((entity) => (
                                <MenuItem
                                    key={`${entity.type}-${entity._id}`}
                                    onClick={() => handleEntitySelect(entity)}
                                    sx={{
                                        py: 1.25,
                                        px: 1.5,
                                        borderBottom: '1px solid',
                                        borderColor: 'divider',
                                        '&:last-child': { borderBottom: 'none' },
                                        '&:hover': { 
                                            bgcolor: entity.type === 'vendor' ? 'primary.50' : 'secondary.50' 
                                        }
                                    }}
                                >
                                    <Box sx={{ width: '100%' }}>
                                        {/* Entity Name and Type */}
                                        <Box display="flex" justifyContent="space-between" alignItems="center" mb={0.5}>
                                            <Box display="flex" alignItems="center" gap={0.5}>
                                                {getEntityIcon(entity.type)}
                                                <Typography variant="body2" fontWeight="bold" noWrap>
                                                    {entity.name}
                                                </Typography>
                                            </Box>
                                            <Chip
                                                label={entity.type === 'vendor' ? 'Vendor' : 'Party'}
                                                size="small"
                                                color={getEntityColor(entity.type)}
                                                variant="outlined"
                                                sx={{ height: 20, fontSize: '0.7rem' }}
                                            />
                                        </Box>

                                        {/* Contact Info */}
                                        <Stack spacing={0.25}>
                                            {entity.contact?.phone?.[0]?.phoneNo && (
                                                <Typography variant="caption" color="text.secondary" display="flex" alignItems="center" gap={0.5}>
                                                    <PhoneIcon fontSize="inherit" sx={{ opacity: 0.7 }} />
                                                    {entity.contact.phone[0].phoneNo}
                                                </Typography>
                                            )}

                                            {entity.contact?.email?.[0] && (
                                                <Typography variant="caption" color="text.secondary" display="flex" alignItems="center" gap={0.5}>
                                                    <EmailIcon fontSize="inherit" sx={{ opacity: 0.7 }} />
                                                    {entity.contact.email[0]}
                                                </Typography>
                                            )}

                                            {entity.address?.line1 && (
                                                <Typography variant="caption" color="text.secondary" display="flex" alignItems="center" gap={0.5}>
                                                    <LocationOnIcon fontSize="inherit" sx={{ opacity: 0.7 }} />
                                                    {entity.address.line1}
                                                </Typography>
                                            )}
                                        </Stack>
                                    </Box>
                                </MenuItem>
                            ))
                        )}
                    </Box>
                )}

                {/* Add New Button */}
                <Divider />
                <Box sx={{ p: 1 }}>
                    <Button
                        startIcon={<AddIcon />}
                        onClick={() => {
                            handleClose();
                            if (onAddNewVendor) onAddNewVendor();
                        }}
                        variant="contained"
                        fullWidth
                        size="small"
                        sx={{
                            textTransform: 'none',
                            fontSize: '0.875rem',
                            py: 0.75
                        }}
                    >
                        Add New Vendor
                    </Button>
                </Box>
            </Menu>
        </Card>
    );
};

export default VendorSelectSection;